/**
 * @description: 表单容器
 */
import { HelpCircle } from '@tencent/odc-iconpack';
import { Popover } from '@tencent/one-design-next';
import classnames from 'classnames';
import { CSSProperties, FocusEvent, ReactElement, ReactNode, cloneElement, isValidElement } from 'react';

import '../index.scss';

export interface IFormContainerProps {
  /**
   * 类名
   * @default ""
   */
  className?: string;
  /**
   * 表单数据，必须包含 title
   */
  style?: CSSProperties;
  /**
   * label内容
   */
  label?: ReactNode;
  /**
   * 标题的className
   */
  labelClassName?: string;
  /**
   * 描述的className
   */
  descClassName?: string;
  /**
   * 内容部分的className
   */
  contentClassName?: string;
  /**
   * label后面跟着的气泡内容，如果存在，则展示问号气泡
   */
  labelTips?: string | ReactNode;
  /**
   * label右边追加操作内容，将会右对齐，可以传入组件或dom，该配置只有layout为block时才会生效
   */
  labelSupplement?: string | ReactNode;
  /**
   * 描述内容，特别注意：当layout为block时才会展示
   */
  desc?: string;
  /**
   * 有label时，选择横向行内布局还是纵向块级布局。默认为横向行内inline布局
   * @default "block"
   */
  layout?: 'inline' | 'block';

  /**
   * 表单状态
   */
  status?: 'error';
  /**
   * 报错文案
   */
  helpMsg?: string | ReactNode;
  /**
   * children
   */
  children?: ReactNode;
  onBlur?: (e: FocusEvent) => void;
}
const FormContainer = (props: IFormContainerProps) => {
  const {
    className,
    style,
    children,
    layout = 'block',
    label,
    labelClassName,
    desc,
    contentClassName,
    descClassName,
    labelSupplement,
    labelTips,
    onBlur,
    status,
    helpMsg,
  } = props;

  // 代理 children 的 onBlur 方法
  const renderChildren = () => {
    const wrap = (ele: ReactNode) => {
      if (!isValidElement(ele)) {
        return ele;
      }
      const childElement = ele as ReactElement;
      const originalOnBlur = childElement.props?.onBlur;
      return cloneElement(childElement, {
        ...childElement.props,
        onBlur: (e: FocusEvent) => {

          // 调用原始的 onBlur 方法
          if (originalOnBlur) {
            originalOnBlur(e);
          }
          // 在这里可以添加自定义逻辑
          try {
            onBlur?.(e);
          } catch (error) {
            console.error(error);
          }
        },
      });
    };
    return Array.isArray(children) ? children.map(wrap) : wrap(children);
  };
  return (
    <div
      className={classnames('odc-form-container', className, {
        'layout-block': layout === 'block',
        'layout-inline': layout === 'inline',
      })}
      style={style}
    >
      {label && (
        <div
          className={classnames('odc-form-label text-base', labelClassName, {
            '!mb-1r': desc,
            'flex items-center justify-between': labelSupplement,
          })}
        >
          <div
            className={classnames('label', {
              'flex items-center': labelTips,
            })}
          >
            {label}
            {labelTips && (<Popover popup={labelTips}
              placement="bottomLeft"
              offset={{ alignmentAxis: -100 }}
              arrowed={false}
              popupClassName="!p-3r !max-w-400"
            >
              <HelpCircle className="ml-1r cursor-pointer text-base text-grey-8" />
            </Popover>)}
          </div>
          {labelSupplement && (
            <div
              className={classnames('label-supplement')}
            >
              {labelSupplement}
            </div>
          )}
        </div>
      )}
      {desc && (
        <p className={classnames('odc-form-desc', descClassName)}>
          {desc}
        </p>
      )}
      <div className={classnames('odc-form-content', contentClassName, {
        'has-error': status === 'error',
      })}
      >
        {renderChildren()}
      </div>
      {status === 'error' && (
        <div className="odc-form-error-msg text-error text-xs !mt-2r">
          {helpMsg}
        </div>
      )}
    </div>
  );
};

export default FormContainer;
